import React, { useState, useEffect } from 'react';
import Header from '../components/Header.jsx';
import { adminAPI } from '../services/api';

const ImageLibrary = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    category: 'product',
    description: '',
    imageFile: null,
    imageUrl: ''
  });
  const [previewImage, setPreviewImage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const response = await adminAPI.getImageLibrary();
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Error loading images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadData({ ...uploadData, imageFile: reader.result, imageUrl: '' });
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUrlChange = (url) => {
    setUploadData({ ...uploadData, imageUrl: url, imageFile: null });
    setPreviewImage(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!uploadData.imageFile && !uploadData.imageUrl) {
      alert('Please upload an image or enter an image URL');
      return;
    }

    try {
      await adminAPI.uploadToLibrary({
        image_url: uploadData.imageFile || uploadData.imageUrl,
        category: uploadData.category,
        description: uploadData.description
      });

      alert('Image uploaded successfully');
      resetForm();
      loadImages();
    } catch (error) {
      alert('Error uploading image');
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await adminAPI.deleteFromLibrary(imageId);
      loadImages();
      alert('Image deleted successfully');
    } catch (error) {
      alert('Error deleting image');
    }
  };

  const resetForm = () => {
    setUploadData({
      category: 'product',
      description: '',
      imageFile: null,
      imageUrl: ''
    });
    setPreviewImage('');
    setShowUploadForm(false);
  };

  const copyToClipboard = (imageUrl) => {
    navigator.clipboard.writeText(imageUrl);
    setCopySuccess(imageUrl);
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const viewImage = (image) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  return (
    <div>
      <Header />
      <main>
        <section className="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h1 style={{ color: 'var(--maroon)', fontFamily: "'Playfair Display',serif", margin: '0' }}>
              Image Library
            </h1>
            <button
              onClick={() => setShowUploadForm(!showUploadForm)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--maroon)',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              {showUploadForm ? 'Cancel' : '+ Upload New Image'}
            </button>
          </div>

          {/* Upload Form */}
          {showUploadForm && (
            <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
              <h2 style={{ marginTop: '0', color: 'var(--maroon)' }}>Upload New Image</h2>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Category</label>
                    <select
                      value={uploadData.category}
                      onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                      required
                      style={{ width: '100%' }}
                    >
                      <option value="product">Product</option>
                      <option value="event">Event</option>
                      <option value="banner">Banner</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Description</label>
                    <input
                      type="text"
                      value={uploadData.description}
                      onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                      placeholder="Enter image description"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Upload Image</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <label style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: '1px solid var(--maroon)',
                        background: '#fff',
                        color: 'var(--maroon)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'inline-block'
                      }}>
                        📁 Choose File
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                        />
                      </label>
                      {previewImage && uploadData.imageFile && (
                        <img
                          src={previewImage}
                          alt="Preview"
                          style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div style={{ textAlign: 'center', color: '#999', fontSize: '14px' }}>OR</div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Image URL</label>
                    <input
                      type="url"
                      value={uploadData.imageUrl}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      style={{ width: '100%' }}
                    />
                    {previewImage && uploadData.imageUrl && (
                      <img
                        src={previewImage}
                        alt="Preview"
                        style={{
                          width: '100px',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          marginTop: '12px'
                        }}
                      />
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={resetForm}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        background: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'var(--maroon)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Upload Image
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Images Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              Loading images...
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
              {images.map((image) => (
                <div key={image.image_id} className="card" style={{ padding: '12px', position: 'relative' }}>
                  <div
                    style={{
                      width: '100%',
                      height: '150px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: '8px',
                      cursor: 'pointer'
                    }}
                    onClick={() => viewImage(image)}
                  >
                    {image.image_url && (image.image_url.startsWith('http') || image.image_url.startsWith('data:image')) ? (
                      <img
                        src={image.image_url}
                        alt={image.description || 'Library image'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%', 
                        height: '100%', 
                        background: '#f3f4f6', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: '#999'
                      }}>
                        No Image
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0' }}>
                    {image.category}
                  </p>
                  {image.description && (
                    <p style={{ fontSize: '12px', margin: '4px 0', wordBreak: 'break-word' }}>
                      {image.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      onClick={() => copyToClipboard(image.image_url)}
                      style={{
                        flex: 1,
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid var(--maroon)',
                        background: '#fff',
                        color: 'var(--maroon)',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {copySuccess === image.image_url ? '✓ Copied!' : 'Copy URL'}
                    </button>
                    <button
                      onClick={() => deleteImage(image.image_id)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        background: '#dc2626',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {images.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              No images in library. Click "Upload New Image" to get started.
            </div>
          )}
        </section>
      </main>

      {/* Image Modal */}
      {selectedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '20px',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: 'var(--maroon)' }}>Image Details</h3>
              <button
                onClick={closeModal}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#f3f4f6',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
            <img
              src={selectedImage.image_url}
              alt={selectedImage.description}
              style={{
                width: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
                borderRadius: '8px',
                marginBottom: '16px'
              }}
            />
            <div style={{ fontSize: '14px' }}>
              <p><strong>Category:</strong> {selectedImage.category}</p>
              {selectedImage.description && <p><strong>Description:</strong> {selectedImage.description}</p>}
              <p><strong>Uploaded:</strong> {new Date(selectedImage.uploaded_at).toLocaleDateString()}</p>
              <div style={{ marginTop: '12px' }}>
                <strong>Image URL:</strong>
                <div style={{ 
                  background: '#f3f4f6', 
                  padding: '8px', 
                  borderRadius: '4px', 
                  marginTop: '4px',
                  wordBreak: 'break-all',
                  fontSize: '12px'
                }}>
                  {selectedImage.image_url.substring(0, 100)}...
                </div>
                <button
                  onClick={() => copyToClipboard(selectedImage.image_url)}
                  style={{
                    marginTop: '8px',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid var(--maroon)',
                    background: '#fff',
                    color: 'var(--maroon)',
                    cursor: 'pointer'
                  }}
                >
                  {copySuccess === selectedImage.image_url ? '✓ Copied!' : 'Copy Full URL'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageLibrary;
